/* ════════════════════════════════════════════════════════════════════════
 * PAI Portfolio Analytics — track.js v1.0
 * ────────────────────────────────────────────────────────────────────────
 * Single shared analytics library. Drops onto any Shopify theme via
 * theme.liquid. Auto-fires: Page Viewed, Page Scrolled, Section Viewed,
 * CTA Clicked, Form Field Focused, Add to Cart. Exposes window.PAI.track().
 *
 * Required setup BEFORE this script loads:
 *   <script>
 *     window.PAI_BRAND   = 'smartsups';        // 'smartsups' | 'blocq_uk' | ...
 *     window.PAI_FUNNEL  = 'auto';             // 'quiz' | 'advertorial' | 'lp' | 'pdp' | 'auto'
 *     window.PAI_POSTHOG_KEY = 'phc_...';      // public project key
 *     window.PAI_POSTHOG_HOST = 'https://us.i.posthog.com';
 *   </script>
 *   <script src="{{ 'pai-track.js' | asset_url }}" async></script>
 *
 * Source of truth: /home/shared/tools/analytics/track.js
 * Sync to themes: bun run /home/shared/tools/analytics/sync-track.ts <brand>
 * ──────────────────────────────────────────────────────────────────────── */
(function () {
  "use strict";

  if (window.__PAI_TRACK_LOADED__) return;
  window.__PAI_TRACK_LOADED__ = true;

  // ──────────────────────────────────────────────────────────────────────
  // Config + bootstrap
  // ──────────────────────────────────────────────────────────────────────
  var BRAND = window.PAI_BRAND || "unknown";
  var FUNNEL = window.PAI_FUNNEL || "auto";
  var KEY = window.PAI_POSTHOG_KEY;
  var HOST = window.PAI_POSTHOG_HOST || "https://us.i.posthog.com";
  var DEBUG = !!window.PAI_DEBUG;
  var PORTFOLIO_DOMAINS = window.PAI_PORTFOLIO_DOMAINS || [
    "thesmartsups.com",
    "blocq.store",
    "blocqofficial.com",
    "blocqlondon.com",
    "blocq.uk",
    "sittralondon.com"
  ];

  if (!KEY) {
    console.warn("[PAI] PAI_POSTHOG_KEY missing — track.js disabled");
    return;
  }

  // Auto-detect funnel_type from URL when set to 'auto'
  if (FUNNEL === "auto") {
    var p = location.pathname.toLowerCase();
    if (p.indexOf("/products/") === 0) FUNNEL = "pdp";
    else if (p.indexOf("/cart") === 0) FUNNEL = "cart";
    else if (p.indexOf("/checkouts") === 0) FUNNEL = "checkout";
    else if (p.indexOf("/blogs/") === 0) FUNNEL = "advertorial";
    else if (p.indexOf("/find-your-edge") !== -1 || p.indexOf("/quiz") !== -1) FUNNEL = "quiz";
    else if (p.indexOf("/pages/") === 0) FUNNEL = "lp";
    else if (p === "/" || p === "") FUNNEL = "home";
    else FUNNEL = "other";
  }

  // ──────────────────────────────────────────────────────────────────────
  // Distinct ID + session ID (persisted to localStorage)
  // ──────────────────────────────────────────────────────────────────────
  function uuid() {
    return ("10000000-1000-4000-8000-100000000000").replace(/[018]/g, function (c) {
      return (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16);
    });
  }

  function getOrCreate(key, val) {
    try {
      var v = localStorage.getItem(key);
      if (v) return v;
      localStorage.setItem(key, val);
      return val;
    } catch (e) { return val; }
  }

  // Cross-domain stitching: if URL has _phid, adopt it as our distinct_id
  var urlParams = new URLSearchParams(location.search);
  var inboundPhid = urlParams.get("_phid");
  if (inboundPhid) {
    try { localStorage.setItem("pai_distinct_id", inboundPhid); } catch (e) {}
  }

  var distinctId = getOrCreate("pai_distinct_id", "anon_" + uuid());

  // Session: 30-min inactivity window
  var SESSION_TTL = 30 * 60 * 1000;
  var now = Date.now();
  var sessionId, sessionStart;
  try {
    var sLast = parseInt(localStorage.getItem("pai_session_last") || "0", 10);
    sessionId = localStorage.getItem("pai_session_id");
    sessionStart = parseInt(localStorage.getItem("pai_session_start") || "0", 10);
    if (!sessionId || (now - sLast) > SESSION_TTL) {
      sessionId = "sess_" + uuid();
      sessionStart = now;
      localStorage.setItem("pai_session_id", sessionId);
      localStorage.setItem("pai_session_start", String(sessionStart));
    }
    localStorage.setItem("pai_session_last", String(now));
  } catch (e) {
    sessionId = "sess_" + uuid();
    sessionStart = now;
  }

  // ──────────────────────────────────────────────────────────────────────
  // UTM capture (persistent for 30 days — first-touch + last-touch both kept)
  // ──────────────────────────────────────────────────────────────────────
  var UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "fbclid", "gclid", "ttclid"];
  var utmCurrent = {};
  UTM_KEYS.forEach(function (k) {
    var v = urlParams.get(k);
    if (v) utmCurrent[k] = v;
  });
  var utmFirst = {};
  var utmLast = {};
  try {
    utmFirst = JSON.parse(localStorage.getItem("pai_utm_first") || "{}");
    utmLast = JSON.parse(localStorage.getItem("pai_utm_last") || "{}");
    if (Object.keys(utmCurrent).length) {
      utmLast = utmCurrent;
      localStorage.setItem("pai_utm_last", JSON.stringify(utmLast));
      if (!Object.keys(utmFirst).length) {
        utmFirst = utmCurrent;
        localStorage.setItem("pai_utm_first", JSON.stringify(utmFirst));
      }
    }
  } catch (e) {}

  // ──────────────────────────────────────────────────────────────────────
  // Common props (attached to every event)
  // ──────────────────────────────────────────────────────────────────────
  function viewport() { return window.innerWidth + "x" + window.innerHeight; }
  function deviceClass() {
    var w = window.innerWidth;
    if (w < 768) return "mobile";
    if (w < 1100) return "tablet";
    return "desktop";
  }
  function pageHandle() {
    var seg = location.pathname.split("/").filter(Boolean);
    return seg[seg.length - 1] || "home";
  }
  function shopifyTemplate() {
    try { return (window.Shopify && window.Shopify.template) || null; } catch (e) { return null; }
  }

  function commonProps() {
    var p = {
      brand: BRAND,
      funnel_type: FUNNEL,
      page_handle: pageHandle(),
      page_path: location.pathname,
      page_url: location.href,
      page_title: document.title,
      referrer: document.referrer || null,
      session_id: sessionId,
      session_start_iso: new Date(sessionStart).toISOString(),
      device: deviceClass(),
      viewport: viewport(),
      shopify_template: shopifyTemplate(),
      timestamp_iso: new Date().toISOString()
    };
    // Merge UTMs (current call wins, then last, then first as fallback)
    var k;
    for (k in utmFirst) if (!p["utm_first_" + k.replace(/^utm_/, "")]) p["utm_first_" + k.replace(/^utm_/, "")] = utmFirst[k];
    for (k in utmLast) p[k] = utmLast[k];
    for (k in utmCurrent) p[k] = utmCurrent[k];
    return p;
  }

  // ──────────────────────────────────────────────────────────────────────
  // PostHog capture (direct POST — no SDK needed, cuts ~50KB)
  // ──────────────────────────────────────────────────────────────────────
  function capture(eventName, props, opts) {
    var payload = {
      api_key: KEY,
      event: eventName,
      distinct_id: distinctId,
      timestamp: new Date().toISOString(),
      properties: Object.assign({}, commonProps(), props || {}, {
        $current_url: location.href,
        $host: location.host,
        $pathname: location.pathname,
        $referrer: document.referrer || null
      })
    };
    var body = JSON.stringify(payload);
    var fetchOpts = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body,
      mode: "cors",
      credentials: "omit"
    };
    if (opts && opts.keepalive) fetchOpts.keepalive = true;

    if (DEBUG) console.debug("[PAI track]", eventName, payload.properties);

    // Use sendBeacon for keepalive scenarios (page unload, navigation)
    if (opts && opts.keepalive && navigator.sendBeacon) {
      try {
        navigator.sendBeacon(HOST + "/capture/", new Blob([body], { type: "application/json" }));
        return Promise.resolve();
      } catch (e) {}
    }
    return fetch(HOST + "/capture/", fetchOpts).catch(function (err) {
      console.warn("[PAI] capture failed", eventName, err);
    });
  }

  // ──────────────────────────────────────────────────────────────────────
  // Auto: Page Viewed (fired immediately)
  // ──────────────────────────────────────────────────────────────────────
  capture("Page Viewed", {});

  // ──────────────────────────────────────────────────────────────────────
  // Auto: Page Scrolled (25/50/75/100% — fired once per threshold)
  // ──────────────────────────────────────────────────────────────────────
  var scrollThresholds = [25, 50, 75, 100];
  var scrollFired = {};
  function onScroll() {
    var doc = document.documentElement;
    var scrollTop = window.scrollY || doc.scrollTop;
    var scrollHeight = (doc.scrollHeight || document.body.scrollHeight) - window.innerHeight;
    if (scrollHeight <= 0) return;
    var pct = Math.round((scrollTop / scrollHeight) * 100);
    scrollThresholds.forEach(function (t) {
      if (!scrollFired[t] && pct >= t) {
        scrollFired[t] = true;
        capture("Page Scrolled", { scroll_depth_pct: t });
      }
    });
  }
  var scrollTicking = false;
  window.addEventListener("scroll", function () {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(function () { onScroll(); scrollTicking = false; });
  }, { passive: true });

  // ──────────────────────────────────────────────────────────────────────
  // Auto: Section Viewed (IntersectionObserver on [data-section])
  // ──────────────────────────────────────────────────────────────────────
  var sectionFired = {};
  function watchSections() {
    if (!("IntersectionObserver" in window)) return;
    var nodes = document.querySelectorAll("[data-section]");
    if (!nodes.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var name = e.target.getAttribute("data-section");
        var pos = e.target.getAttribute("data-section-position") || null;
        var key = name + "::" + pos;
        if (sectionFired[key]) return;
        sectionFired[key] = true;
        capture("Section Viewed", {
          section_name: name,
          section_position: pos
        });
      });
    }, { threshold: 0.5 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  // ──────────────────────────────────────────────────────────────────────
  // Auto: CTA Clicked ([data-cta], or any <a>/<button> inside [data-cta-region])
  // ──────────────────────────────────────────────────────────────────────
  document.addEventListener("click", function (e) {
    var t = e.target;
    var cta = t.closest && t.closest("[data-cta]");
    if (!cta) return;
    var label = cta.getAttribute("data-cta") || cta.textContent.trim().slice(0, 80);
    var position = cta.getAttribute("data-cta-position") || null;
    var dest = cta.getAttribute("href") || null;
    capture("CTA Clicked", {
      cta_label: label,
      cta_position: position,
      cta_destination: dest
    }, { keepalive: !!dest });
  }, true);

  // ──────────────────────────────────────────────────────────────────────
  // Auto: Form Field Focused (debounced — first focus per field per session)
  // ──────────────────────────────────────────────────────────────────────
  var fieldFired = {};
  document.addEventListener("focusin", function (e) {
    var t = e.target;
    if (!t || (t.tagName !== "INPUT" && t.tagName !== "TEXTAREA" && t.tagName !== "SELECT")) return;
    var fieldName = t.name || t.id || t.getAttribute("placeholder") || t.type;
    if (fieldFired[fieldName]) return;
    fieldFired[fieldName] = true;
    capture("Form Field Focused", {
      field_name: fieldName,
      field_type: t.type,
      form_id: (t.form && (t.form.id || t.form.action)) || null
    });
  }, true);

  // ──────────────────────────────────────────────────────────────────────
  // Auto: Add to Cart detection (Shopify pattern: form[action*="/cart/add"])
  // ──────────────────────────────────────────────────────────────────────
  document.addEventListener("submit", function (e) {
    var f = e.target;
    if (!f || !f.action) return;
    if (f.action.indexOf("/cart/add") === -1) return;
    var qty = parseInt((f.querySelector('[name="quantity"]') || {}).value || "1", 10);
    var variant = (f.querySelector('[name="id"]') || {}).value || null;
    capture("Add to Cart", {
      variant_id: variant,
      quantity: qty,
      cart_form_action: f.action
    }, { keepalive: true });
  }, true);

  // Also catch fetch-based ATC (Shopify AJAX cart)
  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function (url, opts) {
      var r = origFetch.apply(this, arguments);
      try {
        var u = (typeof url === "string") ? url : (url && url.url) || "";
        if (u.indexOf("/cart/add") !== -1 && (!opts || (opts.method || "GET").toUpperCase() === "POST")) {
          r.then(function (resp) {
            if (resp && resp.ok) {
              capture("Add to Cart", { source: "ajax", url: u }, { keepalive: true });
            }
          }).catch(function () {});
        }
      } catch (e) {}
      return r;
    };
  }

  // ──────────────────────────────────────────────────────────────────────
  // Cross-domain link decoration: append _phid to outbound portfolio links
  // so a user clicking from blocq → smartsups stitches into the same person.
  // ──────────────────────────────────────────────────────────────────────
  function decorateLinks() {
    var links = document.querySelectorAll('a[href]');
    links.forEach(function (a) {
      try {
        var u = new URL(a.href, location.href);
        if (u.host === location.host) return;
        for (var i = 0; i < PORTFOLIO_DOMAINS.length; i++) {
          if (u.host.indexOf(PORTFOLIO_DOMAINS[i]) !== -1) {
            if (!u.searchParams.has("_phid")) {
              u.searchParams.set("_phid", distinctId);
              a.href = u.toString();
            }
            break;
          }
        }
      } catch (e) {}
    });
  }

  // ──────────────────────────────────────────────────────────────────────
  // Public API
  // ──────────────────────────────────────────────────────────────────────
  window.PAI = window.PAI || {};
  window.PAI.track = function (name, props, opts) { return capture(name, props, opts); };
  window.PAI.identify = function (email, traits) {
    if (email) {
      try { localStorage.setItem("pai_email", email); } catch (e) {}
      capture("$identify", Object.assign({ $set: Object.assign({ email: email }, traits || {}) }, traits || {}));
    }
  };
  window.PAI.distinctId = distinctId;
  window.PAI.sessionId = sessionId;
  window.PAI.brand = BRAND;
  window.PAI.funnel = FUNNEL;
  window.PAI.config = { key: KEY, host: HOST };

  // ──────────────────────────────────────────────────────────────────────
  // Init: kick off observers after DOM is ready
  // ──────────────────────────────────────────────────────────────────────
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      watchSections();
      decorateLinks();
    });
  } else {
    watchSections();
    decorateLinks();
  }

  // Re-scan sections + decorate links after dynamic content loads
  if (typeof MutationObserver !== "undefined") {
    var rescanTimer = null;
    new MutationObserver(function () {
      clearTimeout(rescanTimer);
      rescanTimer = setTimeout(function () {
        watchSections();
        decorateLinks();
      }, 250);
    }).observe(document.body || document.documentElement, { childList: true, subtree: true });
  }

  if (DEBUG) {
    console.log("[PAI] track.js v1.0 loaded", {
      brand: BRAND, funnel: FUNNEL, distinct_id: distinctId, session_id: sessionId
    });
  }
})();
